import axios, { type InternalAxiosRequestConfig } from "axios";
import { clearSigningKey, ensureSigningKey, initKeyExchange, signJWS } from "./signing";
import { getApiNonce, getPublicNonce } from "./nonce";

const api = axios.create({
  baseURL: "/api/v1",
});

// ----------------------------------------------------------------------------
// Which requests need a nonce + signature?
// ----------------------------------------------------------------------------

const BYPASS_PATHS = ["/auth/nonce", "/auth/key-exchange"];

const PUBLIC_NONCE_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/mfa/verify",
  "/auth/password/reset-request",
  "/auth/password/reset",
  "/auth/email/verify",
  "/materiil-keluar/guest",
];

const WRITE_METHODS = new Set(["post", "put", "delete", "patch"]);

function requiresNonce(url: string, method: string): boolean {
  if (BYPASS_PATHS.some((path) => url.includes(path))) return false;
  return WRITE_METHODS.has(method.toLowerCase());
}

function isPublicEndpoint(url: string): boolean {
  return PUBLIC_NONCE_PATHS.some((path) => url.includes(path));
}

// ----------------------------------------------------------------------------
// Request interceptor
// ----------------------------------------------------------------------------

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url ?? "";
    const method = config.method ?? "get";

    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (!requiresNonce(url, method)) return config;

    if (config.data instanceof FormData) {
      await ensureSigningKey();
      const uploadNonce = await getApiNonce();
      if (uploadNonce) config.headers["X-Nonce-Token"] = uploadNonce;
      return config;
    }

    const pubEndpoint = isPublicEndpoint(url);
    let nonce: string | null;
    let signingKey: CryptoKey | null = null;

    if (pubEndpoint) {
      const existingNonce = config.headers["X-Nonce-Token"] as string | undefined;
      nonce = existingNonce ?? (await getPublicNonce());
    } else {
      signingKey = await ensureSigningKey();
      nonce = await getApiNonce();
    }

    if (!nonce) return config;
    config.headers["X-Nonce-Token"] = nonce;

    if (config.data != null) {
      const body =
        typeof config.data === "string" ? JSON.parse(config.data) : config.data;

      const key = pubEndpoint ? nonce : signingKey;

      if (!key) {
        console.error("[Signing] Tidak ada signing key/nonce untuk", url);
      }

      const signed = key
        ? await signJWS(body, key).catch((e) => {
            console.error("[Signing] Gagal menandatangani request ke", url, e);
            return null;
          })
        : null;

      if (signed) {
        config.data = signed;
        config.headers["Content-Type"] = "text/plain";
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ----------------------------------------------------------------------------
// Response interceptor
// ----------------------------------------------------------------------------
let sessionExpiredDispatched = false;

api.interceptors.response.use(
  (response) => {
    const url = response.config?.url ?? "";
    const data = response.data;

    const justAuthenticated =
      (url.includes("/auth/login") || url.includes("/auth/mfa/verify")) &&
      data?.token &&
      !data?.mfa_required;

    if (justAuthenticated) {
      sessionExpiredDispatched = false;
      clearSigningKey();
      initKeyExchange(data.token as string).catch(() => {});
      window.dispatchEvent(new Event("auth-login"));
    }

    const rotatedToken = response.headers?.["x-new-token"];
    if (rotatedToken) localStorage.setItem("token", rotatedToken);

    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error.config?.url ?? "";
    const method = (error.config?.method ?? "").toLowerCase();

    const isPhotoError = url.includes("/photo") && (status === 404 || status === 500);
    const isMissingAsetLookup =
      method === "get" && status === 404 && /^\/aset\/[^/?]+$/.test(url);
    // Endpoint pengembalian belum diimplementasikan backend — ditangani di service.
    const isMissingKembaliEndpoint =
      method === "get" && status === 404 && url.startsWith("/materiil-kembali");
    const isExpectedAuthFailure = status === 401;
    const isLeafChildrenLookup =
      method === "get" &&
      status === 500 &&
      /^\/identifikasi\/[^/?]+\/children$/.test(url);

    if (
      !isPhotoError &&
      !isMissingAsetLookup &&
      !isMissingKembaliEndpoint &&
      !isExpectedAuthFailure &&
      !isLeafChildrenLookup
    ) {
      // ← tambahkan error.response?.data supaya pesan asli backend kelihatan di console
      console.error("[API Error]", url, status, error.message, error.response?.data);
    }

    if (status === 401 && typeof window !== "undefined") {
      const isAuthRequestItself = url.includes("/pages/login") || url.includes("/auth/login");
      if (!isAuthRequestItself && !sessionExpiredDispatched) {
        sessionExpiredDispatched = true;
        localStorage.removeItem("token");
        clearSigningKey();
        window.dispatchEvent(new Event("session-expired"));
      }
    }

    return Promise.reject(error);
  },
);

export { clearSigningKey, initKeyExchange, getPublicNonce };
export default api;